import { useRef, useEffect, useMemo, useState } from 'react';
import { ScrollView, useWindowDimensions } from 'react-native-web';
import { shallow } from 'zustand/shallow';
import { CityFilter, CustomButton, Text, Pressable, View } from '@rivallapp/volosports-components';

import { MainTabEnum } from '../../../../shared/discover-tabs-enum';
import responsiveStyles from '../../styles';
import { useUrlParams } from '../../../../hooks';
import useDiscoverStore, { DiscoverActions } from '../../../../zustand-stores/DiscoverStore';
import { getProgramTypesFromTabs } from '../../../../shared-components/feature-components';
import useCheckIfFiltersAreApplied from '../../hooks/useCheckIfFiltersAreApplied';
import {
  DiscoverDayFilter,
  DiscoverDateFilter,
  DiscoverTimeFilter,
  DiscoverVenueFilter,
  DiscoverGenderFilter,
  DiscoverActivityFilter,
  DiscoverSkillLevelFilter,
  DiscoverProgramTypeFilter,
  DiscoverNeighborhoodFilter,
  DiscoverTagFilter,
  DiscoverNumberOfPeopleFilter,
  DiscoverVoloPassMembershipPerksFilter,
} from './components';
import discoverStyles from './discoverStyles';

const { clearFilters, toggleShowMobileFilters, updateCityFilter } = DiscoverActions;

const DiscoverFilters = () => {
  const filtersRef = useRef(null);
  const [showCityDropdown, setShowCityDropdown] = useState(false);

  const { view, subView, isDaily, cityName, showMobileFilters } = useDiscoverStore(
    store => ({
      view: store.view,
      subView: store.subView,
      isDaily: store.isDaily,
      cityName: store.filters.cityName,
      showMobileFilters: store.showMobileFilters,
    }),
    shallow
  );

  const isRental = view === MainTabEnum.COURT_RENTALS;

  const programTypes = getProgramTypesFromTabs(view, subView);

  const { replaceUrlParams } = useUrlParams();

  const layout = useWindowDimensions();
  const screenBreakpoint = 1024;
  const screenBreakpointMet = layout.width < screenBreakpoint;
  const styles = responsiveStyles(layout, screenBreakpoint);

  const stylesMemo = useMemo(
    () => [
      styles('filtersContainer'),
      ...(showMobileFilters ? [styles('show')] : [styles('hide')]),
      ...(screenBreakpointMet ? [{ height: layout.height - 92 }] : []),
    ],
    [layout, styles, showMobileFilters, screenBreakpointMet]
  );

  // When in mobile filter view, add an event listener to close the filters when clicking outside of the filters
  useEffect(() => {
    const handleOutsideClick = event => {
      if (filtersRef.current && !filtersRef.current.contains(event.target)) {
        toggleShowMobileFilters();
      }
    };

    if (screenBreakpointMet) {
      document.addEventListener('click', handleOutsideClick);
    }

    return () => {
      document.removeEventListener('click', handleOutsideClick);
    };
  }, [screenBreakpointMet]);

  const filtersAreApplied = useCheckIfFiltersAreApplied();

  const onResetFilters = city => {
    replaceUrlParams({ cityName: city, view, subView });
    clearFilters();
  };

  const toggleCityDropdown = () => {
    setShowCityDropdown(!showCityDropdown);
  };

  const onPressCity = city => {
    onResetFilters(city);
    updateCityFilter(city);
  };

  if (!cityName) return null;

  if (!showMobileFilters && screenBreakpointMet) return null;

  // hide program types filter if only one program type is available
  return (
    <ScrollView style={stylesMemo} ref={filtersRef}>
      {isDaily || isRental ? (
        <View style={discoverStyles.row}>
          <DiscoverDateFilter />
        </View>
      ) : null}
      <View style={discoverStyles.row}>
        {showCityDropdown ? (
          <CityFilter cityName={cityName} onPress={onPressCity} />
        ) : (
          <Text style={discoverStyles.headerTitle}>City: {cityName}</Text>
        )}
        <Pressable onPress={toggleCityDropdown}>
          <Text style={discoverStyles.link}>{showCityDropdown ? 'back' : 'change'}</Text>
        </Pressable>
      </View>
      {filtersAreApplied && (
        <CustomButton
          thin
          value="Reset Filters"
          variant="link"
          textStyle={styles('resetButton')}
          onPress={() => onResetFilters(cityName)}
          style={{ marginLeft: 10 }}
        />
      )}
      <DiscoverActivityFilter />
      <DiscoverDayFilter />
      {isDaily || isRental ? <DiscoverTimeFilter /> : null}
      {!isRental ? <DiscoverGenderFilter /> : null}
      {programTypes.length > 1 ? <DiscoverProgramTypeFilter /> : null}
      <DiscoverNeighborhoodFilter />
      <DiscoverVenueFilter />
      {!isRental ? <DiscoverSkillLevelFilter /> : null}
      {!isRental ? <DiscoverTagFilter /> : null}
      {!isRental ? <DiscoverNumberOfPeopleFilter /> : null}
      {!isRental ? <DiscoverVoloPassMembershipPerksFilter /> : null}
    </ScrollView>
  );
};

export default DiscoverFilters;
